import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { config, goToMembershipHome, homeMembership } from '../lib/config';
import { useSession } from '../lib/store';
import { PLATFORM_NAME, PLATFORM_TAGLINE } from '../lib/brand';
import { Spinner } from '../components/Shell';
import { AppMockup } from '../components/marketing/AppMockup';
import { AvatarRow, DogPortrait, Sparks } from '../components/marketing/Illustrations';
import {
  ArrowRight,
  CalendarIcon,
  CapIcon,
  CardIcon,
  ChartIcon,
  Check,
  ChevronDown,
  DogIcon,
  HomeIcon,
  Paw,
  PawOutline,
  PlayCircle,
  ScissorsIcon,
  ShieldIcon,
  SitterIcon,
  Sparkle,
  Star,
} from '../components/marketing/icons';

/**
 * The platform's own marketing site: the page a pet professional lands on
 * before they have a business, and the only screen in the product with nothing
 * real behind it.
 *
 * It does not use the paper design the rest of the app is built on. That
 * design is the argument for the product once you are inside it; this page has
 * to make the argument from outside, and it is dressed accordingly. Its
 * palette is the --color-promo-* set, which nothing else may touch.
 *
 * Everything shown is either drawn (the dashboard, the phone, the dog) or a
 * real link. There are no invented customer numbers, no fake testimonials with
 * a stranger's photograph attached, and no nav item that leads nowhere: every
 * entry in the bar is a section further down this page.
 *
 * A pet owner cannot start here. Their account, pets and bookings are scoped to
 * one sitter, so the honest thing is to send them back to the link they were
 * given rather than offer a sign-up that has nowhere to attach.
 */
export function PlatformHome() {
  // Someone who has already visited a sitter's page has the slug remembered, so
  // "I have a link" can actually take them back rather than just explaining.
  const remembered = readRememberedSlug();
  const session = useSession();
  const home = homeMembership(session.memberships);

  // Someone who already has a business does not need to be sold one, and being
  // shown a sales page with a log-in link on it reads as having been signed
  // out. Their business is on its own hostname, so this is a departure rather
  // than a route change, and it happens in an effect because it is one.
  useEffect(() => {
    if (home) goToMembershipHome(home);
  }, [home]);

  if (home) {
    return (
      <div className="promo mx-auto max-w-md px-6 py-32">
        <Spinner label={`Opening ${home.businessName}`} />
      </div>
    );
  }

  return (
    <div className="promo">
      <MarketingHeader />
      <Hero />
      <IndustryStrip />
      <Features />
      <HowItWorks />
      <Pricing />
      <Faq domain={config.appDomain} remembered={remembered} />
      <ClosingBanner />
      <MarketingFooter />
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Header
   ------------------------------------------------------------------------ */

const NAV_LINKS = [
  { href: '#features', label: 'Features' },
  { href: '#industries', label: 'Industries' },
  { href: '#how', label: 'How it works' },
  { href: '#pricing', label: 'Pricing' },
  { href: '#faq', label: 'FAQ' },
];

function MarketingHeader() {
  const [open, setOpen] = useState(false);
  // Anyone with a business has already been sent to it, so whoever is signed in
  // here holds an account and no business yet: offering them a way to log in is
  // an answer to a question they are not asking. Sign out is, in case they are
  // holding the wrong account.
  const session = useSession();
  const signedIn = session.user !== null;

  return (
    <header className="sticky top-0 z-40 border-b border-[color:var(--color-promo-line)] bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-6 px-5 py-3.5">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <Paw className="h-7 w-7 text-[color:var(--color-promo)]" />
          <span className="font-display text-[1.35rem] font-extrabold tracking-[-0.045em]">
            {PLATFORM_NAME}
          </span>
        </Link>

        <nav
          className="hidden flex-1 items-center justify-center gap-7 text-[0.94rem] font-medium lg:flex"
          aria-label="Marketing"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-[color:var(--color-promo-ink)] hover:text-[color:var(--color-promo)]"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          {signedIn ? (
            <button
              type="button"
              className="promo-btn promo-btn-ghost hidden sm:inline-flex"
              onClick={() => session.signOut()}
            >
              Sign out
            </button>
          ) : (
            <Link to="/sign-in" className="promo-btn promo-btn-ghost hidden sm:inline-flex">
              Log in
            </Link>
          )}
          <Link to="/start-a-business" className="promo-btn promo-btn-primary">
            {signedIn ? 'Set up my business' : 'Get started'}
          </Link>
          <button
            type="button"
            className="promo-btn promo-btn-outline px-3 lg:hidden"
            aria-expanded={open}
            aria-controls="promo-menu"
            onClick={() => setOpen((was) => !was)}
          >
            <span className="sr-only">Menu</span>
            <ChevronDown
              className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="promo-menu"
          className="border-t border-[color:var(--color-promo-line)] px-5 py-3 lg:hidden"
          aria-label="Marketing, compact"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="block py-2 font-medium"
            >
              {link.label}
            </a>
          ))}
          {signedIn ? (
            <button
              type="button"
              className="block py-2 font-medium sm:hidden"
              onClick={() => {
                setOpen(false);
                session.signOut();
              }}
            >
              Sign out
            </button>
          ) : (
            <Link to="/sign-in" className="block py-2 font-medium sm:hidden" onClick={() => setOpen(false)}>
              Log in
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}

/* ---------------------------------------------------------------------------
   Hero
   ------------------------------------------------------------------------ */

/** Each point sets its own line breaks, so the four labels stay level. */
const HERO_POINTS = [
  { icon: CalendarIcon, lines: ['Online booking', '24/7'] },
  { icon: PawOutline, lines: ['Client & pet', 'management'] },
  { icon: CardIcon, lines: ['Payments &', 'invoicing'] },
  { icon: ChartIcon, lines: ['Business Insights'] },
];

function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* A single soft wash behind the mockup, so the drawn panels have
          something to sit on without a hard band across the page. */}
      <div
        aria-hidden
        className="pointer-events-none absolute right-[-10%] top-[-14%] h-[460px] w-[560px] rounded-full bg-[color:var(--color-promo-tint)] blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-14 lg:grid-cols-[1fr_1.06fr] lg:py-20">
        <div className="min-w-0">
          <p className="promo-pill">
            <Sparkle className="h-4 w-4" />
            All-in-one booking &amp; business management
          </p>

          <h1 className="mt-6 text-[clamp(2.25rem,4.1vw,3.1rem)] leading-[1.04]">
            The booking platform
            <br />
            <span className="text-[color:var(--color-promo)]">built for pet businesses</span>
          </h1>

          <p className="mt-5 max-w-lg text-[1.06rem] leading-relaxed text-[color:var(--color-promo-body)]">
            {PLATFORM_NAME} helps pet service providers save time, stay organised, and delight
            more pet parents.
          </p>

          <ul className="mt-9 grid grid-cols-2 gap-x-6 gap-y-7 sm:grid-cols-4">
            {HERO_POINTS.map((point) => (
              <li key={point.lines[0]} className="min-w-0">
                <point.icon className="h-7 w-7 text-[color:var(--color-promo)]" />
                <p className="mt-2.5 text-[0.88rem] font-semibold leading-snug">
                  {point.lines.map((line, index) => (
                    <span key={line}>
                      {index > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link to="/start-a-business" className="promo-btn promo-btn-primary">
              Set up my business
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#how" className="promo-btn promo-btn-ghost">
              See how it works
              <PlayCircle className="h-[1.15rem] w-[1.15rem]" />
            </a>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <AvatarRow />
            <div className="min-w-0">
              <div className="flex gap-0.5 text-[#e3a92c]">
                {[0, 1, 2, 3, 4].map((star) => (
                  <Star key={star} className="h-4 w-4" />
                ))}
              </div>
              <p className="text-[0.86rem] text-[color:var(--color-promo-body)]">
                Built with a working pet sitter, from her own paperwork
              </p>
            </div>
          </div>
        </div>

        <div className="min-w-0 lg:pb-10 xl:-mr-6">
          <AppMockup />
          <p className="mt-11 text-center text-[0.72rem] text-[color:var(--color-promo-body)] sm:mt-14">
            Illustration of the sitter dashboard. Figures shown are examples.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Industries
   ------------------------------------------------------------------------ */

const INDUSTRIES = [
  { icon: SitterIcon, label: 'Pet sitters' },
  { icon: DogIcon, label: 'Dog walkers' },
  { icon: ScissorsIcon, label: 'Groomers' },
  { icon: HomeIcon, label: 'Daycares' },
  { icon: CapIcon, label: 'Trainers' },
  { icon: PawOutline, label: '& more' },
];

function IndustryStrip() {
  return (
    <section id="industries" className="scroll-mt-20 px-5 pb-4">
      <div className="mx-auto max-w-7xl rounded-2xl bg-[color:var(--color-promo-mist)] px-6 py-8">
        <h2 className="text-center text-[1.05rem] font-bold">Perfect for every pet business</h2>
        <ul className="mt-6 grid grid-cols-2 gap-y-6 sm:grid-cols-3 lg:grid-cols-6">
          {INDUSTRIES.map((industry) => (
            <li key={industry.label} className="flex min-w-0 items-center justify-center gap-2.5">
              <industry.icon className="h-6 w-6 shrink-0 text-[color:var(--color-promo)]" />
              <span className="truncate text-[0.95rem] font-medium">{industry.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Features
   ------------------------------------------------------------------------ */

const FEATURES = [
  {
    icon: CalendarIcon,
    tint: 'var(--color-promo-tile-violet)',
    title: 'Easy online booking',
    body: 'Let clients book services 24/7 against your real availability.',
    href: '#how',
  },
  {
    icon: PawOutline,
    tint: 'var(--color-promo-tile-mint)',
    title: 'Client & pet profiles',
    body: 'Feeding, medication, vaccinations and the vet\u2019s number, kept on the animal\u2019s own record.',
    href: '#how',
  },
  {
    icon: CardIcon,
    tint: 'var(--color-promo-tile-amber)',
    title: 'Payments & invoicing',
    body: 'Take deposits and balances by card, send invoices, and let reminders chase them for you.',
    href: '#pricing',
  },
  {
    icon: ShieldIcon,
    tint: 'var(--color-promo-tile-sky)',
    title: 'Signed agreements',
    body: 'Your agreement goes out by email and is signed on their phone. Nothing printed or scanned.',
    href: '#faq',
  },
  {
    icon: PlayCircle,
    tint: 'var(--color-promo-tile-violet)',
    title: 'Private video sharing',
    body: 'Send a client a lesson, or a clip of their pet having fun, that only they can open, on a link that expires. Not an unlisted upload.',
    href: '#faq',
  },
  {
    icon: ChartIcon,
    tint: 'var(--color-promo-tile-sky)',
    title: 'Insights & reports',
    body: 'Track bookings, money actually received, and which services earn their place in your week.',
    href: '#how',
  },
];

function Features() {
  return (
    <section id="features" className="scroll-mt-20 px-5 py-16 lg:py-20">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.86fr_2.14fr]">
        <header className="min-w-0">
          <h2 className="text-[clamp(1.8rem,2.9vw,2.2rem)] leading-[1.1]">
            Everything you need.
            <br />
            <span className="text-[color:var(--color-promo)]">In one simple platform.</span>
          </h2>
          <p className="mt-4 max-w-xs text-[color:var(--color-promo-body)]">
            {PLATFORM_NAME} is designed to help you run your business smoothly, so you can focus
            on pets, not paperwork.
          </p>
          <Link to="/start-a-business" className="promo-btn promo-btn-primary mt-7">
            Set up my business
            <ArrowRight className="h-4 w-4" />
          </Link>
        </header>

        <ul className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {FEATURES.map((feature) => (
            <li key={feature.title} className="promo-card min-w-0">
              <span className="promo-tile" style={{ background: feature.tint }}>
                <feature.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-4 text-[1.05rem]">{feature.title}</h3>
              <p className="mt-2 text-[0.92rem] leading-relaxed text-[color:var(--color-promo-body)]">
                {feature.body}
              </p>
              <a href={feature.href} className="promo-link mt-4">
                Learn more
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   How it works
   ------------------------------------------------------------------------ */

const STEPS = [
  {
    title: 'Set up your business',
    body: 'Name, service area, and the services you actually offer with the rates you actually charge. Ten minutes, no card.',
  },
  {
    title: 'Share your link',
    body: 'You get a booking page at your own address. Send it to the clients you already have; new ones find it too.',
  },
  {
    title: 'Let it run the admin',
    body: 'Requests, pet records, vaccination reminders, the signed agreement and the money all land in one place.',
  },
];

function HowItWorks() {
  return (
    <section id="how" className="scroll-mt-20 bg-[color:var(--color-promo-mist)] px-5 py-16 lg:py-20">
      <div className="mx-auto max-w-7xl">
        <header className="max-w-2xl">
          <p className="promo-pill">How it works</p>
          <h2 className="mt-5 text-[clamp(1.9rem,3.4vw,2.5rem)] leading-[1.08]">
            From paperwork to a booking page,
            <br className="hidden sm:block" /> in three steps.
          </h2>
        </header>

        <ol className="mt-10 grid gap-4 lg:grid-cols-3">
          {STEPS.map((step, index) => (
            <li key={step.title} className="promo-card min-w-0 bg-white">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--color-promo)] font-display text-[0.95rem] font-extrabold text-white">
                {index + 1}
              </span>
              <h3 className="mt-4 text-[1.05rem]">{step.title}</h3>
              <p className="mt-2 text-[0.92rem] leading-relaxed text-[color:var(--color-promo-body)]">
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Pricing
   ------------------------------------------------------------------------ */

const PLANS = [
  {
    name: 'Solo',
    price: 'Free',
    note: 'forever, plus 5% of what you take by card',
    points: [
      'Your own booking page',
      'Unlimited pet records',
      'Signed agreements',
      'Vaccination reminders',
      'Card payments and invoicing, by card through Stripe',
      'Google Calendar sync',
    ],
    cta: 'Set up my business',
    featured: false,
  },
  {
    name: 'Professional',
    price: '$29',
    note: 'per month, and no platform fee on card payments',
    points: [
      'Everything in Solo',
      'The 5% card fee goes away',
      'Take Zelle, Venmo or cash as well',
      'Private video sharing',
      'Your own domain name',
      'Booking and revenue reports',
    ],
    cta: 'Start on Professional',
    featured: true,
  },
  {
    name: 'Team',
    price: '$59',
    note: 'per month',
    points: [
      'Everything in Professional',
      'Staff accounts and roles',
      'Per-sitter schedules',
      'Priority support',
    ],
    cta: 'Start on Team',
    featured: false,
  },
];

function Pricing() {
  return (
    <section id="pricing" className="scroll-mt-20 px-5 py-16 lg:py-20">
      <div className="mx-auto max-w-7xl">
        <header className="mx-auto max-w-2xl text-center">
          <p className="promo-pill">Pricing</p>
          <h2 className="mt-5 text-[clamp(1.9rem,3.4vw,2.5rem)] leading-[1.08]">
            Start free. Pay us once it pays for itself.
          </h2>
          <p className="mt-4 text-[color:var(--color-promo-body)]">
            No platform fee on your bookings on a paid plan. Solo costs nothing a month and
            takes 5% of card payments instead. Card processing comes out of the payment either
            way, the same as it would anywhere else.
          </p>
        </header>

        <div className="mt-10 grid items-start gap-4 lg:grid-cols-3">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`promo-card min-w-0 ${
                plan.featured
                  ? 'border-[color:var(--color-promo)] shadow-[0_18px_44px_-26px_rgba(109,71,232,0.75)] lg:-mt-3 lg:pb-8'
                  : ''
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-[1.05rem]">{plan.name}</h3>
                {plan.featured && (
                  <span className="rounded-full bg-[color:var(--color-promo-tint)] px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[color:var(--color-promo-deep)]">
                    Most chosen
                  </span>
                )}
              </div>
              <p className="mt-4 font-display text-[2.4rem] font-extrabold leading-none tracking-[-0.04em]">
                {plan.price}
              </p>
              <p className="mt-1 text-[0.86rem] text-[color:var(--color-promo-body)]">{plan.note}</p>

              <ul className="mt-5 space-y-2.5">
                {plan.points.map((point) => (
                  <li key={point} className="flex min-w-0 gap-2.5 text-[0.92rem]">
                    <Check className="mt-[3px] h-4 w-4 shrink-0 text-[color:var(--color-promo)]" />
                    <span className="min-w-0">{point}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/start-a-business"
                className={`promo-btn mt-6 w-full ${
                  plan.featured ? 'promo-btn-primary' : 'promo-btn-outline'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        <p className="mt-6 text-center text-[0.8rem] text-[color:var(--color-promo-body)]">
          Professional costs less than Solo once you are taking about $580 a month by card,
          which is somewhere around a dozen visits. Prices are indicative while the platform is
          in build, and nothing is gated by plan yet.
        </p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   FAQ, which is also where a pet owner who landed here gets sent home
   ------------------------------------------------------------------------ */

function Faq({ domain, remembered }: { domain: string; remembered: string | null }) {
  const questions = [
    {
      q: 'I am a pet owner, not a sitter. Am I in the right place?',
      a: (
        <>
          Not quite. Your pets and bookings belong to your sitter, so you start from the link they
          sent you, something like{' '}
          <code className="rounded bg-[color:var(--color-promo-tint)] px-1.5 py-0.5 font-mono text-[0.85em]">
            <ExampleHost host={`theirname.${domain}`} />
          </code>
          .
        </>
      ),
    },
    {
      q: 'Do my clients need to create an account?',
      a: 'To book, yes, and it takes a moment. To sign your agreement, no: the link you email them is the credential, so they can read and sign it on their phone without a password.',
    },
    {
      q: 'How is sharing a video different from an unlisted YouTube link?',
      a: 'An unlisted link never expires, works for anyone it is forwarded to, and cannot be taken back. Here the file is never public: your client signs in, and the player gets a link that dies after five minutes. Deleting the video revokes it for good.',
    },
    {
      q: 'Can I use my own domain name?',
      a: 'Yes. Every business gets an address on the platform straight away, and you can point your own domain at your booking page once you have one.',
    },
    {
      q: 'What happens to my records if I leave?',
      a: 'They are yours. Pets are archived rather than deleted, so past bookings, agreements and vaccination records stay intact, and an owner can export the whole lot as one file whenever they want it.',
    },
  ];

  return (
    <section id="faq" className="scroll-mt-20 px-5 py-16 lg:py-20">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
        <header className="min-w-0">
          <p className="promo-pill">FAQ</p>
          <h2 className="mt-5 text-[clamp(1.9rem,3.4vw,2.4rem)] leading-[1.08]">
            The things people ask first.
          </h2>
          {remembered && (
            <Link to={`/b/${remembered}`} className="promo-btn promo-btn-outline mt-6">
              Back to my sitter
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </header>

        <dl className="min-w-0 divide-y divide-[color:var(--color-promo-line)] border-y border-[color:var(--color-promo-line)]">
          {questions.map((item) => (
            <div key={item.q} className="py-5">
              <dt className="font-display text-[1.02rem] font-extrabold tracking-[-0.02em]">
                {item.q}
              </dt>
              <dd className="mt-2 text-[0.95rem] leading-relaxed text-[color:var(--color-promo-body)]">
                {item.a}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
   Closing banner and footer
   ------------------------------------------------------------------------ */

function ClosingBanner() {
  return (
    <section className="px-5 pb-16 lg:pb-20">
      <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 overflow-hidden rounded-2xl bg-[color:var(--color-promo-tint)] px-6 py-8 sm:px-10 lg:pl-[300px]">
        {/* The dog leans in from the left on a wide screen and is dropped
            entirely on a narrow one, where it would push the copy off. */}
        <div aria-hidden className="absolute bottom-0 left-6 hidden items-end lg:flex">
          <Sparks className="mb-16 h-8 w-8 text-[color:var(--color-promo)]" />
          <DogPortrait className="h-[156px] w-auto" />
          <Sparks className="mb-16 h-8 w-8 -scale-x-100 text-[color:var(--color-promo)]" />
        </div>

        <div className="min-w-0">
          <h2 className="text-[clamp(1.4rem,2.6vw,1.85rem)]">Ready to grow your pet business?</h2>
          <p className="mt-2 max-w-lg text-[0.98rem] text-[color:var(--color-promo-body)]">
            Set up your business, share your link, and let {PLATFORM_NAME} handle the paperwork
            from the first booking on.
          </p>
        </div>

        <Link to="/start-a-business" className="promo-btn promo-btn-primary shrink-0">
          Set up my business
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

/** A footer entry is either an in-page anchor or a route, never both. */
type FooterLink = { label: string; href: string; to?: never } | { label: string; to: string; href?: never };

const FOOTER_COLUMNS: { heading: string; links: FooterLink[] }[] = [
  {
    heading: 'Product',
    links: [
      { href: '#features', label: 'Features' },
      { href: '#pricing', label: 'Pricing' },
      { href: '#how', label: 'How it works' },
      { href: '#industries', label: 'Industries' },
    ],
  },
  {
    heading: 'For pet businesses',
    links: [
      { to: '/start-a-business', label: 'Set up my business' },
      { to: '/sign-in', label: 'Log in' },
      { href: '#faq', label: 'FAQ' },
    ],
  },
];

function MarketingFooter() {
  // Same as the header: nobody reading this page is signed out and short of a
  // way in, or signed in and short of their business.
  const signedIn = useSession((state) => state.user !== null);
  const columns = signedIn
    ? FOOTER_COLUMNS.map((column) => ({
        ...column,
        links: column.links.filter((link) => link.to !== '/sign-in'),
      }))
    : FOOTER_COLUMNS;

  return (
    <footer className="border-t border-[color:var(--color-promo-line)] px-5 py-10">
      <div className="mx-auto grid max-w-7xl gap-8 sm:grid-cols-[1.6fr_1fr_1fr]">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Paw className="h-6 w-6 text-[color:var(--color-promo)]" />
            <span className="font-display text-[1.15rem] font-extrabold tracking-[-0.045em]">
              {PLATFORM_NAME}
            </span>
          </div>
          <p className="mt-3 max-w-xs text-[0.9rem] text-[color:var(--color-promo-body)]">
            {PLATFORM_TAGLINE}
          </p>
        </div>

        {columns.map((column) => (
          <div key={column.heading} className="min-w-0">
            <p className="text-[0.78rem] font-bold uppercase tracking-[0.1em] text-[color:var(--color-promo-body)]">
              {column.heading}
            </p>
            <ul className="mt-3 space-y-2 text-[0.92rem]">
              {column.links.map((link) => (
                <li key={link.label}>
                  {link.to ? (
                    <Link to={link.to} className="hover:text-[color:var(--color-promo)]">
                      {link.label}
                    </Link>
                  ) : (
                    <a href={link.href} className="hover:text-[color:var(--color-promo)]">
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mx-auto mt-9 max-w-7xl border-t border-[color:var(--color-promo-line)] pt-5 text-[0.82rem] text-[color:var(--color-promo-body)]">
        {PLATFORM_NAME} is a working name and not yet a registered brand.
      </div>
    </footer>
  );
}

/**
 * A hostname long enough to wrap. Breaking anywhere splits it mid-label
 * ("kineticfl / ux.net"), so the break opportunities are put after the dots and
 * nowhere else.
 */
function ExampleHost({ host }: { host: string }) {
  const labels = host.split('.');
  return (
    <span>
      {labels.map((label, index) => (
        <span key={`${label}-${index}`}>
          {label}
          {index < labels.length - 1 && (
            <>
              .<wbr />
            </>
          )}
        </span>
      ))}
    </span>
  );
}

function readRememberedSlug(): string | null {
  try {
    return localStorage.getItem('petservices.tenantSlug');
  } catch {
    // Private browsing can throw on access rather than return null.
    return null;
  }
}
